// © Microsoft Corporation. All rights reserved.

import { KeyValueArray, appEnvironment, getHeadData, oneServiceQueryStringKeys } from "@msnews/core";
import { OneServiceUtility, getServiceUrlForSubpath, sendRequest } from "@msnews/oneservice";
import { includeCacheBypassIfNeeded, updateCacheBypassValue } from "@msnews/oneservice-content";

import { Publisher, PublisherActions } from "./Publisher";
import { SignedInStatus } from "@msnews/experiences-constants";
import { getUserSignInState } from "@msnews/auth";
import { isSapphire, getHttpAsync, postHttpAsync, deleteHttpAsync, getUserConfig, getOneServiceHeaders } from "@msnews/sapphire-feed-bridge";

/**
 * A class that provides service wrappers for OneService endpoints needed to be used by Publisher management experience.
 *
 * @export
 */
export class PublisherServiceClient {
    /**
     * The service end point for Followed Providers
     */
    private followedPublishersEndpoint = "v1/News/Users/Me/PreferredProviders";

    /**
     * The service end point for Providers
     */
    private publishersServiceEndpoint = "Msn/Providers";

    /**
     * The service end point for Actions
     */
    private actionsServiceEndpoint = "Graph/Actions";

    /**
     * the ocid for request
     */
    private ocid = "feeds";

    /**
     * Initializes a new instance of the `PublisherServiceClient` class.
     * @param {any} fetchImpl - The fetch implementation
     * @param {boolean} addPageInfoToOcid - Flag to add page info to ocid or not, default is true
     * @returns {PublisherServiceClient}
     */
    public constructor(private fetchImpl: any, private addPageInfoToOcid: boolean = true) {
    }

    /**
     * Returns the user muted publishers from OneSerivce
     *
     * @returns {Promise<Response>} A promise to fulfill returning array of User Muted Publishers
     */
    public async getUserMutedPublishers(): Promise<Publisher[]> {
        const userPublisherMutedActionsResponse = await this.getUserActions(PublisherActions.Mute);

        if (!userPublisherMutedActionsResponse ||
            !userPublisherMutedActionsResponse.value) {
            return null;
        }

        // Return empty array in case we dont have Muted Publishers
        if (!userPublisherMutedActionsResponse.value.length) {
            return [];
        }

        const userPublisherMutedActions = userPublisherMutedActionsResponse.value;

        const publisherMutedActionsMap = userPublisherMutedActions.reduce((map, action) => { map[action.targetId] = action; return map; }, {});

        const publisherDetailsResponse = await this.getPublisherDetails(Object.keys(publisherMutedActionsMap));

        if (!publisherDetailsResponse ||
            !publisherDetailsResponse.value ||
            !publisherDetailsResponse.value.length) {
            return null;
        }

        const publisherDetails = publisherDetailsResponse.value;

        const publishers = publisherDetails.map(detail => {
            const actionDate = { createdDateTime: publisherMutedActionsMap[detail.id].createdDateTime };

            return {
                ...detail,
                ...actionDate
            } as Publisher;
        });

        return publishers;
    }

    /**
     * Returns the user followed publishers from OneSerivce
     *
     * @param count - number of followed publishers
     * @returns {Promise<Response>} A promise to fulfill returning array of User Followed Publishers
     */
    public async getUserFollowedPublishers(count = 100): Promise<Publisher[]> {
        const requestParams: RequestInit = await this.getRequestInit();
        const commonParams: KeyValueArray<string, string> = await this.getCommonParams();
        const query: KeyValueArray<string, string> = [
            ...commonParams,
            {
                key: oneServiceQueryStringKeys.top,
                value: `${count}`
            }
        ];

        const serviceUrl = getServiceUrlForSubpath(this.followedPublishersEndpoint);
        this.appendQs(query, serviceUrl);

        if (!isSapphire()) {
            includeCacheBypassIfNeeded(serviceUrl);
        }

        // Send the request, using the ServiceRequest utility, which manages retries, logging, etc.
        const publishersResponse = await this.sendRequest(decodeURIComponent(serviceUrl.href), requestParams, "getFollowedPublishers");
        return publishersResponse?.value;
    }

    /**
     * Send publisher action request to OneSerivce
     * @param providerId - provider id
     * @param actionType - oneservice actionType
     * @param method - request method
     *
     * @returns {Promise<Response>} A promise to fulfill returning response for delete User Action
     */
    public async publisherUserAction(targetId: string, actionType: PublisherActions, method: string): Promise<Response> {
        if (!targetId || !actionType) {
            return null;
        }

        const requestParams = await this.getRequestInit();
        requestParams.method = method;
        let commonParams: KeyValueArray<string, string> = await this.getCommonParams();

        if (method === "POST") {
            requestParams.body = JSON.stringify({
                actionType,
                targetId,
                targetType: "SourceProvider"
            });
        } else if (method === "DELETE") {
            commonParams = [
                ...commonParams,
                {
                    key: oneServiceQueryStringKeys.filter,
                    value: `actionType eq '${actionType}' and targetId eq '${targetId}'`
                }
            ];
        }

        const serviceUrl = getServiceUrlForSubpath(this.actionsServiceEndpoint);
        this.appendQs(commonParams, serviceUrl);

        const rawResponse = await this.sendRequest(decodeURIComponent(serviceUrl.href), requestParams, `deleteUserAction ${actionType}`, true, true);
        if (rawResponse.status >= 400) {
            const errMsg = `OneService returned error response. Status: ${rawResponse.status} for actionType: ${actionType}, targetId: ${targetId}`;
            throw new Error(errMsg);
        }
        return rawResponse;
    }

    /**
     * Get publisher status from OneSerivce
     * @param providerId - provider id
     * @param actionType - oneservice actionType
     *
     * @returns {Promise<boolean>} A promise to fulfill returning status
     */
    public async getUserPublisherStatus(targetId: string, actionType: PublisherActions): Promise<boolean> {
        const requestParams = await this.getRequestInit();
        requestParams.method = "GET";

        const params = [
            ...await this.getCommonParams(),
            {
                key: oneServiceQueryStringKeys.filter,
                value: `actionType eq '${actionType}' and targetId eq '${targetId}'`
            }
        ];

        const serviceUrl = getServiceUrlForSubpath(this.actionsServiceEndpoint);
        this.appendQs(params, serviceUrl);

        if (!isSapphire()) {
            includeCacheBypassIfNeeded(serviceUrl);
        }

        const response = await this.sendRequest(decodeURIComponent(serviceUrl.href), requestParams, `getUserPublisherStatus ${actionType}`);
        return !!response.value?.length;
    }

    /**
     * Gets the user actions list from OneSerivce
     *
     * @returns {Promise<any>} A promise to fulfill returning the json with User Actions
     */
    private async getUserActions(actionType: PublisherActions, count = 100): Promise<any> {

        const requestParams = await this.getRequestInit();
        const commonParams: KeyValueArray<string, string> = await this.getCommonParams();

        const query: KeyValueArray<string, string> = [
            ...commonParams,
            {
                key: oneServiceQueryStringKeys.filter,
                value: `actionType eq '${actionType}' and deleted eq false`
            },
            {
                key: oneServiceQueryStringKeys.top,
                value: `${count}`
            }
        ];

        const serviceUrl = getServiceUrlForSubpath(this.actionsServiceEndpoint);
        this.appendQs(query, serviceUrl);

        if (!isSapphire()) {
            includeCacheBypassIfNeeded(serviceUrl);
        }

        return await this.sendRequest(decodeURIComponent(serviceUrl.href), requestParams, `getUserActions ${actionType}`);
    }

    /**
     * Gets the publishers details from OneSerivce
     *
     * @returns {Promise<any>} A promise to fulfill returning the json with Publisher Details
     */
    private async getPublisherDetails(publisherIds: string[]): Promise<any> {

        const requestParams = await this.getRequestInit();

        const serviceUrl = getServiceUrlForSubpath(this.publishersServiceEndpoint);

        const commonParams: KeyValueArray<string, string> = OneServiceUtility.getOneServiceParamsWithoutAuth(null, this.ocid, this.addPageInfoToOcid);

        const query: KeyValueArray<string, string> = [
            ...commonParams,
            {
                key: oneServiceQueryStringKeys.ids,
                value: publisherIds.join(",")
            }
        ];

        this.appendQs(query, serviceUrl);

        return await this.sendRequest(decodeURIComponent(serviceUrl.href), requestParams, "getPublisherDetails");
    }

    /**
     * Populates the request init and return commonParams
     * @returns {KeyValueArray<string, string>}
     */
    private async getCommonParams(): Promise<KeyValueArray<string, string>> {
        if (isSapphire()) {
            return this.getSapphireQueryParams();
        }
        const userIsSignedIn: boolean = await this.isUserSignedIn();
        return userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(appEnvironment.UserId, this.ocid, this.addPageInfoToOcid) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(appEnvironment.UserId, this.ocid, this.addPageInfoToOcid);
    }

    /**
     * Returns query params.
     */
    private getSapphireQueryParams(): KeyValueArray<string, string> {
        // Static params. This includes apikey, ocid
        const queryParams = OneServiceUtility.getOneServiceNonDynamicParamsWithoutAuth(this.ocid, this.addPageInfoToOcid);

        // Dynamic params. This includes, market, fdhead, activityid, and location
        const userConfig = getUserConfig();
        if (userConfig) {
            queryParams.push({
                key: oneServiceQueryStringKeys.market,
                value: userConfig.market
            });
            queryParams.push({
                key: oneServiceQueryStringKeys.fdhead,
                value: userConfig.features
            });
            queryParams.push({
                key: oneServiceQueryStringKeys.activityId,
                value: userConfig.activityId
            });

            if (userConfig.latitude && userConfig.longitude) {
                queryParams.push({
                    key: oneServiceQueryStringKeys.location,
                    value: `${userConfig.latitude}|${userConfig.longitude}`
                });
            }
        }

        return queryParams;
    }

    /**
     * Populates the request init, defaults to GET
     * @param requestInit -- when user is signed in will set the "credentials".
     * @returns - RequestInit init'd with "credentials" if user is signed in.
     */
    private async getRequestInit(requestInit: RequestInit = null): Promise<RequestInit> {
        if (requestInit == null) {
            requestInit = {
                method: "GET"
            };
        }

        if (await this.isUserSignedIn()) {
            requestInit.credentials = "include";
        }

        requestInit.headers = isSapphire() ? await getOneServiceHeaders() : await OneServiceUtility.getOneServiceHeaders();

        return requestInit;
    }

    /**
     * Get user signed in status
     */
    private async isUserSignedIn(): Promise<boolean> {
        // Support for Primehybrid
        const userIsSignedIn = getHeadData().UserIsSignedIn;
        if (userIsSignedIn !== undefined) {
            return userIsSignedIn;
        }

        // Support for Peregrine
        const userSignInState = await getUserSignInState();
        return userSignInState === SignedInStatus.SignedIn;
    }

    /**
     * Make OneService request.
     * @param url
     * @param requestParams
     * @param activityName
     * @param updateBypassValue
     * @param skipToJson
     */
    private async sendRequest(url: string, requestParams: RequestInit, activityName: string, updateBypassValue?: boolean, skipToJson?: boolean): Promise<any> {
        return isSapphire()
            ? await this.sapphireRequest(decodeURIComponent(url), requestParams)
            : await this.networkRequest(decodeURIComponent(url), requestParams, activityName, updateBypassValue, skipToJson);
    }

    /**
     * Make OneService call through Sapphire bridge.
     * @param url
     * @param requestParams
     */
    private async sapphireRequest(url: string, requestParams: RequestInit): Promise<string> {
        const metadata: any = {
            url: url,
            headers: requestParams.headers,
            body: requestParams.body,
            refresh: true
        };

        let response: string;

        switch (requestParams.method) {
            case "POST":
                response = await postHttpAsync(metadata);
                break;
            case "DELETE":
                response = await deleteHttpAsync(metadata);
                break;
            default:
                response = await getHttpAsync(metadata);
                break;
        }

        return JSON.parse(response || "{}");
    }

    /**
     * Make OneService call through network.
     * @param url
     * @param requestParams
     * @param activityName
     * @param updateBypassValue
     * @param skipToJson
     */
    private async networkRequest(url: string, requestParams: RequestInit, activityName: string, updateBypassValue?: boolean, skipToJson?: boolean): Promise<Response> {
        return (
            sendRequest(
                async () => {
                    const response: Response = await this.fetchImpl(url, requestParams);
                    if (!response.ok) {
                        throw Error(response.statusText);
                    }
                    if (updateBypassValue) {
                        updateCacheBypassValue();
                    }
                    return skipToJson ? response : response.json();
                },
                activityName
            )
        );
    }

    /**
     * Append querystring to service url
     * @param query
     * @param serviceUrl
     */
    private appendQs(query: KeyValueArray<string, string>, serviceUrl: URL): void {
        query.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });
    }
}